import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useRecordContext } from 'react-admin';
import AppMockup, { defaultSettings } from '../../appArt/components/AppMockup';
import { HomeSectionView } from '../../appArt/components/homeSections';
import { HomeItem, HomeSectionDef } from './homeItems';

/**
 * A Home do app desenhada a partir do que está no editor.
 *
 * Reaproveita o mockup do estúdio de arte: a mesma tela de 390pt, com as seções
 * ilustradas na cor da rede. Aqui ele recebe a lista configurada em vez da home
 * de exemplo — e é isso que a lista ao lado não consegue mostrar: a ordem vista
 * de cima, o título que o cliente vai LER e a arte dos blocos no formato real.
 */

const PREVIEW_WIDTH = 300;

// Içado porque é renderizado dentro de um `{cond && <JSX/>}`, e ali o plugin de
// i18n embrulha os valores do `sx` — "center" traduzido quebraria o alinhamento.
const EMPTY_SX = { py: 6, textAlign: 'center', fontSize: 12 };

interface HomePreviewProps {
  items: HomeItem[];
  /** O mesmo mapa que o editor já usa para rotular as linhas. */
  sectionById: Map<string, HomeSectionDef>;
}

/**
 * Lista do editor → o que o mockup sabe desenhar.
 *
 * Os títulos saem daqui CRUS, sem `__t`: `defaultTitle` é o literal cravado no
 * build do app, que só fala português, e `title` é texto do operador. Traduzir
 * qualquer um dos dois faria a prévia prometer uma tela que não existe.
 */
const toSectionViews = (items: HomeItem[], sectionById: Map<string, HomeSectionDef>): HomeSectionView[] =>
  items.flatMap<HomeSectionView>(item => {
    if (item.visible === false) { return []; }

    if (item.kind === 'section') {
      return [{ kind: 'section', id: item.id, title: item.title || sectionById.get(item.id)?.defaultTitle }];
    }

    const images = (item.blocks || [])
      .filter(block => !!block.imagePath)
      .map(block => ({ id: block.id, src: block.imagePath as string }));

    // Linha sem nenhuma arte escolhida não chega a existir no app.
    return images.length
      ? [{ kind: 'row', id: item.id, title: item.title || null, layout: item.layout || 'full', images }]
      : [];
  });

const HomePreview: React.FC<HomePreviewProps> = ({ items, sectionById }) => {
  // O record desta tela é a StationChain inteira (o GET de chains-config responde
  // `{ ...chain }`), então cor, logo e slogan já estão na mão — buscar de novo
  // traria o cadastro inteiro, credenciais de integração incluídas.
  const chain = useRecordContext();
  const sections = useMemo(() => toSectionViews(items, sectionById), [items, sectionById]);

  if (!chain) { return null; }

  const settings = defaultSettings({
    color: chain.appColor,
    headerLabelColor: chain.appHeaderLabelColor,
    signUpButtonColor: chain.appSignUpButtonColor,
    signInButtonColor: chain.appSignInButtonColor,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <AppMockup
        screen="home"
        art={null}
        logo={chain.logoPath || null}
        name={chain.appNameInsideApp || chain.appName || chain.name}
        slogan={chain.appSlogan || ''}
        settings={settings}
        width={PREVIEW_WIDTH}
        sections={sections}
        scrollable
      />
      {sections.length === 0 && (
        <Typography color="text.secondary" sx={EMPTY_SX}>Nada visível na Home.</Typography>
      )}
      <Typography color="text.secondary" sx={{ fontSize: 11 }}>
        Ilustrações de exemplo; o conteúdo real vem do cadastro da rede.
      </Typography>
    </Box>
  );
};

// A prévia é a parte cara desta tela (dezenas de SVGs): sem o memo ela redesenha
// a cada tecla digitada no título de uma linha, que grava no formulário.
export default React.memo(HomePreview);
